/**
 * Cuerpo del modal de un pedido (ADM-09): la linea de estado arriba y debajo
 * una tarjeta plegable por tema (lo comprado, cliente, entrega, pago, Random y
 * correos). Lo que no aplica (facturacion con boleta, direccion con retiro,
 * pago sin pagar) no se dibuja: un bloque de guiones obliga a leerlo para
 * descubrir que no dice nada.
 */
import type { ReactNode } from 'react';

import type { DireccionFacturacion, PedidoAdminDetalle } from '@elcarpintero/compartido';

import { fechaHoraCompleta, numero, pesos } from '../../utilidades/formato';
import { formatearRut } from '../../utilidades/rut';
import {
  documento,
  ESTADO_PAGO,
  estadoCorreo,
  rotuloRandom,
  TIPO_CORREO,
  tipoPago,
  unidadesPedido,
} from '../../utilidades/textos-pedido';
import { ItemsPedido } from './ItemsPedido';
import { PasosPedido } from './PasosPedido';
import { TarjetaPedido } from './TarjetaPedido';

export interface DetallePedidoProps {
  pedido: PedidoAdminDetalle;
  volver: string;
}

interface DatoProps {
  nombre: string;
  children: ReactNode;
}

function Dato({ nombre, children }: DatoProps) {
  return (
    <div>
      <dt>{nombre}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Fecha({ iso }: { iso: string }) {
  return <time dateTime={iso}>{fechaHoraCompleta(iso)}</time>;
}

function direccionEnLinea(pedido: PedidoAdminDetalle): string | null {
  const direccion = pedido.direccion;
  if (direccion === null) return null;
  const calle = [direccion.calle, direccion.numero].join(' ');
  return direccion.departamento !== null ? `${calle}, depto. ${direccion.departamento}` : calle;
}

function direccionFacturacionEnLinea(direccion: DireccionFacturacion): string {
  const calle = [direccion.calle, direccion.numero].join(' ');
  const conDepto = direccion.departamento !== null ? `${calle}, depto. ${direccion.departamento}` : calle;
  return `${conDepto}, ${direccion.comuna}, ${direccion.region}`;
}

/**
 * Si la boleta o factura le llego al cliente: el ultimo correo del documento
 * (un documento reemplazado manda otro). Sin documento no se dibuja nada. El
 * PDF no se puede abrir desde aca: el sitio no lo guarda, lo archiva Random.
 */
function EnvioDocumento({ pedido }: { pedido: PedidoAdminDetalle }) {
  if (pedido.folioDocumento === null) return null;

  const correo = pedido.correos.filter((fila) => fila.tipo === 'documento_emitido').at(-1);
  if (correo === undefined) {
    return <Dato nombre="Envío al cliente">Sin correo registrado</Dato>;
  }

  const estado = estadoCorreo(correo);
  return (
    <Dato nombre="Envío al cliente">
      <span className={estado.clase}>{estado.texto}</span>{' '}
      {correo.enviadoEn !== null ? (
        <span className="partible">
          el <Fecha iso={correo.enviadoEn} />, a {correo.destinatario}
        </span>
      ) : null}
    </Dato>
  );
}

function DatosCliente({ pedido }: { pedido: PedidoAdminDetalle }) {
  return (
    <dl className="datos-pedido">
      <Dato nombre="Nombre">{pedido.contacto.nombre}</Dato>
      <Dato nombre="Correo">
        <a href={`mailto:${pedido.contacto.email}`}>{pedido.contacto.email}</a>
      </Dato>
      <Dato nombre="Teléfono">
        <a href={`tel:${pedido.contacto.telefono.replace(/\s/g, '')}`}>{pedido.contacto.telefono}</a>
      </Dato>
      {pedido.rutComprador !== null ? <Dato nombre="RUT">{formatearRut(pedido.rutComprador)}</Dato> : null}
      <Dato nombre="Promociones">
        {pedido.aceptaPromociones ? 'Acepta recibirlas' : 'No las aceptó'}
      </Dato>
      {pedido.facturacion !== null ? (
        <>
          <Dato nombre="RUT empresa">{formatearRut(pedido.facturacion.rut)}</Dato>
          <Dato nombre="Razón social">{pedido.facturacion.razonSocial}</Dato>
          <Dato nombre="Giro">{pedido.facturacion.giro}</Dato>
          {pedido.facturacion.direccion !== null ? (
            <Dato nombre="Dirección de facturación">
              <span className="partible">{direccionFacturacionEnLinea(pedido.facturacion.direccion)}</span>
            </Dato>
          ) : null}
        </>
      ) : null}
    </dl>
  );
}

function DatosEntrega({ pedido }: { pedido: PedidoAdminDetalle }) {
  return (
    <dl className="datos-pedido">
      <Dato nombre="Tipo">
        {pedido.tipoEntrega === 'retiro' ? 'Retiro en tienda' : 'Despacho a domicilio'}
      </Dato>
      {pedido.direccion !== null ? (
        <>
          <Dato nombre="Dirección">{direccionEnLinea(pedido)}</Dato>
          <Dato nombre="Comuna">
            {pedido.direccion.comuna}, {pedido.direccion.region}
          </Dato>
          {pedido.direccion.referencia !== null ? (
            <Dato nombre="Referencia">{pedido.direccion.referencia}</Dato>
          ) : null}
        </>
      ) : null}
      <Dato nombre="Costo">{pedido.costoDespacho > 0 ? pesos(pedido.costoDespacho) : 'Gratis'}</Dato>
    </dl>
  );
}

function DatosPago({ pedido }: { pedido: PedidoAdminDetalle }) {
  const pago = ESTADO_PAGO[pedido.estado];
  const { pago: webpay } = pedido;

  return (
    <dl className="datos-pedido">
      <Dato nombre="Estado">
        <span className={pago.clase}>{pago.texto}</span>
      </Dato>
      {pedido.pagadoEn !== null ? (
        <Dato nombre="Pagado el">
          <Fecha iso={pedido.pagadoEn} />
        </Dato>
      ) : null}
      {webpay.tipoPago !== null ? (
        <Dato nombre="Medio">
          {tipoPago(webpay.tipoPago)}
          {webpay.cuotas !== null && webpay.cuotas > 1 ? `, ${numero(webpay.cuotas)} cuotas` : ''}
        </Dato>
      ) : null}
      {webpay.ultimosDigitosTarjeta !== null ? (
        <Dato nombre="Tarjeta">
          <span className="mono">**** {webpay.ultimosDigitosTarjeta}</span>
        </Dato>
      ) : null}
      {webpay.codigoAutorizacion !== null ? (
        <Dato nombre="Autorización">
          <span className="mono">{webpay.codigoAutorizacion}</span>
        </Dato>
      ) : null}
      {webpay.montoAutorizado !== null ? (
        <Dato nombre="Monto autorizado">{pesos(webpay.montoAutorizado)}</Dato>
      ) : null}
      {webpay.ordenCompra !== null ? (
        <Dato nombre="Orden de compra">
          <span className="mono">{webpay.ordenCompra}</span>
        </Dato>
      ) : null}
      {pedido.tokenTransaccion !== null ? (
        <Dato nombre="Token">
          <span className="mono partible">{pedido.tokenTransaccion}</span>
        </Dato>
      ) : null}
    </dl>
  );
}

function DatosRandom({ pedido }: { pedido: PedidoAdminDetalle }) {
  const random = rotuloRandom(pedido);

  return (
    <dl className="datos-pedido">
      <Dato nombre="Estado">
        <span className={random.clase}>{random.texto}</span>
      </Dato>
      {pedido.random.notaVenta !== null ? (
        <Dato nombre="Nota de venta">
          <span className="mono">{pedido.random.notaVenta}</span>
        </Dato>
      ) : null}
      {pedido.random.registradoEn !== null ? (
        <Dato nombre="Tomado el">
          <Fecha iso={pedido.random.registradoEn} />
        </Dato>
      ) : null}
      <Dato nombre="Documento">{documento(pedido)}</Dato>
      {pedido.random.fechaDocumento !== null ? (
        <Dato nombre="Emitido el">
          <Fecha iso={pedido.random.fechaDocumento} />
        </Dato>
      ) : null}
      <EnvioDocumento pedido={pedido} />
      {pedido.random.ultimoError !== null ? (
        <Dato nombre="Último error">
          <span className="texto-critico partible">{pedido.random.ultimoError}</span>
        </Dato>
      ) : null}
    </dl>
  );
}

function ListaCorreos({ pedido }: { pedido: PedidoAdminDetalle }) {
  if (pedido.correos.length === 0) {
    return <p className="sub">Este pedido no tiene correos registrados.</p>;
  }

  return (
    <ul className="correos-pedido">
      {pedido.correos.map((correo) => {
        const estado = estadoCorreo(correo);
        return (
          <li key={correo.id}>
            <div>
              <span className="n">{TIPO_CORREO[correo.tipo]}</span>
              <span className="s partible">
                A {correo.destinatario} · <Fecha iso={correo.enviadoEn ?? correo.creadoEn} />
              </span>
              {correo.ultimoError !== null && correo.estado !== 'enviado' ? (
                <span className="s texto-critico partible">{correo.ultimoError}</span>
              ) : null}
            </div>
            <span className={estado.clase}>{estado.texto}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function DetallePedido({ pedido, volver }: DetallePedidoProps) {
  // Solo Productos nace abierta; los correos tambien si alguno fallo, para que se vea.
  const correoFallido = pedido.correos.some((correo) => correo.estado === 'fallido');

  return (
    <div className="detalle-pedido">
      <PasosPedido pedido={pedido} />

      <div className="tarjetas-pedido">
        <TarjetaPedido icono="caja" titulo="Productos" detalle={unidadesPedido(pedido)} abierta ancha>
          <ItemsPedido pedido={pedido} volver={volver} />
        </TarjetaPedido>

        <TarjetaPedido icono="persona" titulo="Cliente">
          <DatosCliente pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido icono={pedido.tipoEntrega === 'retiro' ? 'tienda' : 'camion'} titulo="Entrega">
          <DatosEntrega pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido icono="tarjeta" titulo="Pago" detalle="(Webpay)">
          <DatosPago pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido icono="factura" titulo="Random y documento">
          <DatosRandom pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido
          icono="correo"
          titulo="Correos"
          detalle={`(${numero(pedido.correos.length)})`}
          abierta={correoFallido}
          ancha
        >
          <ListaCorreos pedido={pedido} />
        </TarjetaPedido>
      </div>
    </div>
  );
}
